// /home/labdoo/zitech-nexus/frontend/src/pages/admin/portfolio/AdminSolutionsList.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioAPI } from '../../../services/api';
import './PortfolioAdmin.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Star: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  ),
  StarOutline: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Check: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  X: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  ),
  Icon: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
};

interface Solution {
  id: string;
  title: string;
  description: string;
  icon_name: string;
  is_featured: boolean;
  is_published: boolean;
  order: number;
  created_at: string;
}

const AdminSolutionsList: React.FC = () => {
  const navigate = useNavigate();
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'unpublished'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchSolutions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await portfolioAPI.adminGetSolutions();
      setSolutions(response.data);
    } catch (err: any) {
      console.error('Error fetching solutions:', err);
      setError('Failed to load solutions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSolutions();
  }, [fetchSolutions, refreshKey]);

  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this solution? This action cannot be undone.')) {
      try {
        await portfolioAPI.adminDeleteSolution(id);
        await refreshData();
        alert('Solution deleted successfully!');
      } catch (err: any) {
        console.error('Error deleting solution:', err);
        alert('Failed to delete solution');
      }
    }
  };

  const handleToggleFeature = async (id: string, currentStatus: boolean) => {
    setUpdatingId(id);
    try {
      await portfolioAPI.adminUpdateSolution(id, { is_featured: !currentStatus });
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshData();
    } catch (err: any) {
      console.error('Error toggling feature:', err);
      alert('Failed to update solution featured status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    setUpdatingId(id);
    try {
      await portfolioAPI.adminUpdateSolution(id, { is_published: !currentStatus });
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshData();
    } catch (err: any) {
      console.error('Error toggling publish:', err);
      alert('Failed to update solution status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOrderChange = async (id: string, newOrder: number) => {
    if (isNaN(newOrder)) return;
    setUpdatingId(id);
    try {
      await portfolioAPI.adminUpdateSolution(id, { order: newOrder });
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshData();
    } catch (err: any) {
      console.error('Error updating order:', err);
      alert('Failed to update solution order');
    } finally {
      setUpdatingId(null);
    }
  };

  const getIconDisplayName = (iconName: string) => {
    const icons: Record<string, string> = {
      'digital_transformation': 'Digital Transformation',
      'cloud_migration': 'Cloud Migration',
      'cybersecurity': 'Cybersecurity',
      'data_analytics': 'Data Analytics',
      'devops': 'DevOps',
      'ai_ml': 'AI/ML',
    };
    return icons[iconName] || iconName;
  };

  const getStatusColor = (isPublished: boolean) => {
    return isPublished ? '#10b981' : '#f59e0b';
  };

  const getStatusBackground = (isPublished: boolean) => {
    return isPublished ? '#d1fae5' : '#fef3c7';
  };

  const filteredSolutions = solutions.filter(solution => {
    const matchesSearch = solution.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          solution.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'published' && solution.is_published) ||
                         (filterStatus === 'unpublished' && !solution.is_published);
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: solutions.length,
    published: solutions.filter(s => s.is_published).length,
    unpublished: solutions.filter(s => !s.is_published).length,
    featured: solutions.filter(s => s.is_featured).length,
  };

  if (loading) {
    return (
      <div className="pa-loading-container">
        <div className="pa-spinner">
          <Icons.Spinner />
        </div>
        <p className="pa-loading-text">Loading solutions...</p>
      </div>
    );
  }

  return (
    <div className="pa-container">
      {/* Header */}
      <div className="pa-header">
        <div className="pa-header-left">
          <h1 className="pa-title">Manage Solutions</h1>
          <p className="pa-subtitle">Create and manage your portfolio solutions</p>
        </div>
        <button 
          className="pa-btn-primary"
          onClick={() => navigate('/dashboard/admin/portfolio/solutions/create')}
        >
          <Icons.Plus />
          Create New Solution
        </button>
      </div>

      {/* Stats Bar */}
      <div className="pa-stats-bar">
        <div className="pa-stat-item">
          <span className="pa-stat-label">Total</span>
          <span className="pa-stat-value">{stats.total}</span>
        </div>
        <div className="pa-stat-divider"></div>
        <div className="pa-stat-item">
          <span className="pa-stat-label">Published</span>
          <span className="pa-stat-value pa-stat-published">{stats.published}</span>
        </div>
        <div className="pa-stat-divider"></div>
        <div className="pa-stat-item">
          <span className="pa-stat-label">Unpublished</span>
          <span className="pa-stat-value pa-stat-unpublished">{stats.unpublished}</span>
        </div>
        <div className="pa-stat-divider"></div>
        <div className="pa-stat-item">
          <span className="pa-stat-label">Featured</span>
          <span className="pa-stat-value pa-stat-featured">{stats.featured}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="pa-filters">
        <div className="pa-search">
          <Icons.Search />
          <input
            type="text"
            placeholder="Search solutions by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pa-filter-group">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
            <option value="all">All Status</option>
            <option value="published">Published Only</option>
            <option value="unpublished">Unpublished Only</option>
          </select>
          <button className="pa-refresh-btn" onClick={refreshData} disabled={loading}>
            <Icons.Refresh />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="pa-error">{error}</div>}

      {/* Table */}
      <div className="pa-table-wrapper">
        {filteredSolutions.length === 0 ? (
          <div className="pa-empty-state">
            <Icons.Empty />
            <h3 className="pa-empty-title">No solutions found</h3>
            <p className="pa-empty-subtitle">Try adjusting your search or filters, or create a new solution.</p>
            <button className="pa-btn-primary" onClick={() => navigate('/dashboard/admin/portfolio/solutions/create')}>
              <Icons.Plus />
              Create New Solution
            </button>
          </div>
        ) : (
          <table className="pa-table">
            <thead>
              <tr>
                <th className="pa-col-title">Title</th>
                <th className="pa-col-icon">Icon</th>
                <th className="pa-col-desc">Description</th>
                <th className="pa-col-status">Status</th>
                <th className="pa-col-featured">Featured</th>
                <th className="pa-col-order">Order</th>
                <th className="pa-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSolutions.map((solution) => (
                <tr key={`${solution.id}-${solution.is_published}`} className={!solution.is_published ? 'pa-row-inactive' : ''}>
                  <td className="pa-col-title">
                    <span className="pa-project-title">
                      {solution.title}
                      {!solution.is_published && (
                        <span className="pa-draft-badge">Draft</span>
                      )}
                      {updatingId === solution.id && (
                        <span className="pa-spinner-small"></span>
                      )}
                    </span>
                  </td>
                  <td className="pa-col-icon">
                    <span className="pa-icon-badge">
                      <Icons.Icon />
                      {getIconDisplayName(solution.icon_name)}
                    </span>
                  </td>
                  <td className="pa-col-desc">
                    <span className="pa-description-text">
                      {solution.description.substring(0, 100)}...
                    </span>
                  </td>
                  <td className="pa-col-status">
                    <button 
                      className={`pa-status-toggle ${solution.is_published ? 'pa-status-active' : 'pa-status-inactive'}`}
                      onClick={() => handleTogglePublish(solution.id, solution.is_published)}
                      disabled={updatingId === solution.id}
                      title={solution.is_published ? 'Unpublish' : 'Publish'}
                    >
                      <span 
                        className="pa-status-badge"
                        style={{
                          background: getStatusBackground(solution.is_published),
                          color: getStatusColor(solution.is_published)
                        }}
                      >
                        {solution.is_published ? <Icons.Check /> : <Icons.X />}
                        {solution.is_published ? 'Published' : 'Unpublished'}
                      </span>
                    </button>
                  </td>
                  <td className="pa-col-featured">
                    <button 
                      className={`pa-feature-btn ${solution.is_featured ? 'pa-feature-active' : ''}`}
                      onClick={() => handleToggleFeature(solution.id, solution.is_featured)}
                      disabled={updatingId === solution.id}
                      title={solution.is_featured ? 'Remove from featured' : 'Mark as featured'}
                    >
                      {solution.is_featured ? <Icons.Star /> : <Icons.StarOutline />}
                    </button>
                  </td>
                  <td className="pa-col-order">
                    <input 
                      type="number" 
                      value={solution.order} 
                      className="pa-order-input"
                      onChange={(e) => {
                        const newOrder = parseInt(e.target.value);
                        if (!isNaN(newOrder)) {
                          handleOrderChange(solution.id, newOrder);
                        }
                      }}
                      disabled={updatingId === solution.id}
                    />
                  </td>
                  <td className="pa-col-actions">
                    <div className="pa-actions">
                      <button 
                        className="pa-action-btn pa-action-edit"
                        onClick={() => navigate(`/dashboard/admin/portfolio/solutions/${solution.id}/edit`)}
                        title="Edit solution"
                      >
                        <Icons.Edit />
                        Edit
                      </button>
                      <button 
                        className="pa-action-btn pa-action-delete"
                        onClick={() => handleDelete(solution.id)}
                        title="Delete solution"
                        disabled={updatingId === solution.id}
                      >
                        <Icons.Delete />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pa-footer">
        <span className="pa-footer-text">
          Showing {filteredSolutions.length} of {solutions.length} solutions
        </span>
      </div>
    </div>
  );
};

export default AdminSolutionsList;